// DraaidagenOverzicht.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL, API_ENDPOINTS } from '../../utils/apiConfig';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import nlLocale from '@fullcalendar/core/locales/nl';
import { useMsal } from "@azure/msal-react";
import { Box, Tooltip, Grid, Dialog, DialogContent, DialogActions, List, ListItem, ListItemText, Typography, Popover, TextField, Button, IconButton, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import WeekSelector from '../WeekSelector';
import LogoMetTerugval from '../LogoMetTerugval';
import { DraaidagTooltipKaart, DraaidagPilInhoud, ReserveringStatusChip, TOOLTIP_KAART_PROPS, RESERVERING_STATUS, pilKleuren, PIL_FONT, PIL_REGELHOOGTE, JOUW_RAND_CSS, extraDagenSegment, DraaidagOmschrijvingTekst } from './DraaidagPil';
import DubbeleReserveringenDialog from './DubbeleReserveringenDialog';
import { toast } from 'react-hot-toast';
import MismatchedReservations from './MismatchedReservations';
import { set } from 'lodash';
import { useMaterialReservation } from './MaterialReservationContext';
import { MaterialenOverzicht } from './MaterialenOverzicht';
import PageHeader from '../PageHeader';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/nl';
import { formatDateISO } from '../../utils/weekUtils';
import { crewSamengevoegd } from '../../utils/draaidagCrew';
import { draaidagOmschrijving } from '../../utils/draaidagOmschrijving';
import { useDraaidagWeer } from '../../utils/draaidagWeer';
import { WeerChip, WeerRegel } from './WeerChip';
import { mogelijkDubbeleDraaidagen } from '../../utils/dubbeleDraaidagen';
moment.locale('nl');

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e4e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};
const SIcon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

// Custom Event component die een echte link rendert (voor "Open in nieuw tabblad")
const CustomEvent = ({ event }) => {
  const draaidagId = event.record?.id;
  
  return (
    <Tooltip 
      title={
        <div style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>
          {event.tooltipText || event.fullTitle || event.title}
        </div>
      }
      arrow 
      enterDelay={400}
      placement="top"
    >
      <Link 
        to={`/MaterialReservation?draaidagId=${draaidagId}`}
        style={{ 
          color: 'inherit', 
          textDecoration: 'none',
          display: 'block',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.85em',
          lineHeight: '1.3'
        }}
        onClick={(e) => {
          // Voorkom standaard link navigatie bij normale klik (wordt afgehandeld door onSelectEvent)
          // Maar sta Ctrl+Click en Middle-click toe voor nieuw tabblad
          if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
            e.preventDefault();
          }
        }}
      >
        {event.title}
      </Link>
    </Tooltip>
  );
};

// moment is configured at top of file

const projectTypeColors = {
  "Video": "#FFD1DC",    // Pastel Pink
  "Testimonial": "#FFE4D4", // Pastel Peach
  "Interview": "#FFF4CC",  // Pastel Yellow
  "Registratie": "#DCFFD1",  // Pastel Green
  "Registratie met interviewer": "#D1FFEB",  // Pastel Turquoise
  "Aftermovie": "#D1F2FF",  // Pastel Blue
  "TV Commercial": "#DAD1FF",  // Pastel Lavender
  "Online Commercial": "#FFD1F4",  // Pastel Magenta
  "Animatiefilm": "#FFCCF2",  // Pastel Rose
  "Infographic": "#F2D1FF",  // Pastel Purple
  "Webinar": "#FFEBD1",  // Pastel Apricot
  "Explainer": "#F9D1FF",  // Pastel Mauve
  "Campagne": "#E8D1FF",  // Pastel Lilac
  "Podcast": "#FFD9D6",  // Pastel Coral
  "Vlog": "#FFE0D1",  // Pastel Tangerine
  "Fotografie": "#D9E9FF",  // Pastel Periwinkle
  // ... you can continue adding more mappings
};

// Kleuren per reserveringsstatus: zie RESERVERING_STATUS in ./DraaidagPil (legenda hieronder).

// Account info cache (shared across tooltips)
const accountCache = {};

// Losse reserveringen (zonder draaidag): één item op de agenda zetten zonder dat er
// een draaidag aan hangt. Staat aan sinds augustus 2026 — de AI engine telt ze nu ook
// mee in de beschikbaarheidscheck, dus ze kunnen niet meer stil dubbel geboekt worden.
// Zet op false om aanmaken, gele kalender-events, slepen en het legenda-item te verbergen.
const LOSSE_RESERVERINGEN_ENABLED = true;

// Tooltip-kaart voor kalender-events: gedeelde kaart uit DraaidagPil (ook de lijst
// gebruikt dezelfde status-chip). Leest de klant alleen uit de cache — geen API-calls
// vanuit tooltips.
const EventTooltipContent = ({ tooltipText, weer }) => {
  const data = React.useMemo(() => {
    try { return JSON.parse(tooltipText); } catch { return null; }
  }, [tooltipText]);
  if (!data) return <span>{tooltipText}</span>;
  const klant = data.klantAccountId ? accountCache[data.klantAccountId] : null;
  return (
    <Box>
      <DraaidagTooltipKaart data={data} klant={klant} />
      <WeerRegel weer={weer} />
    </Box>
  );
};

// Balk met extra dagen (−1 · draaidag · +1); de verdeling per zichtbaar stuk komt uit
// extraDagenSegment (DraaidagPil). Binnen de "+n meer"-popover is elk stuk één dag.
const OPTIE_ARCERING = 'repeating-linear-gradient(135deg, rgba(255,255,255,0.6) 0 6px, rgba(255,255,255,0.15) 6px 12px)';

function ExtraDagenBalk({ startStr, endStr, isStart, isEnd, heeftVoor, heeftNa, bgColor, txtColor, optie = false, children }) {
  const ref = useRef(null);
  const [meting, setMeting] = useState({ inPopover: false, zichtbareDagen: null });
  // Meet na elke render én bij elke maatverandering van het event: FullCalendar kan
  // hetzelfde stuk (zelfde key, zelfde isStart/isEnd) bij "+n meer"-herindeling inkorten of
  // verlengen (Codex-review 30 sep). setMeting alleen bij een echte wijziging → geen lus.
  const meet = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (el.closest('.fc-popover')) {
      setMeting((m) => (m.inPopover ? m : { inPopover: true, zichtbareDagen: null }));
      return;
    }
    // Welke dagkolommen dekt dit stuk echt? Vergelijk de rechthoek van het event met de
    // dagcellen in dezelfde weekrij (td[data-date]). Zonder layout (tests) blijft het leeg
    // en valt extraDagenSegment terug op de weekgrens-berekening.
    const ev = el.closest('.fc-event') || el;
    const rij = el.closest('tr');
    const r = ev.getBoundingClientRect();
    if (!rij || r.width < 1) return;
    const dagen = Array.from(rij.querySelectorAll('td[data-date]'))
      .filter((td) => { const c = td.getBoundingClientRect(); return c.right > r.left + 2 && c.left < r.right - 2; })
      .map((td) => td.getAttribute('data-date'));
    if (!dagen.length) return;
    setMeting((m) => (!m.inPopover && (m.zichtbareDagen || []).join() === dagen.join()
      ? m : { inPopover: false, zichtbareDagen: dagen }));
  }, []);
  React.useLayoutEffect(() => { meet(); });
  React.useEffect(() => {
    const ev = ref.current?.closest('.fc-event');
    if (!ev || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => meet());
    ro.observe(ev);
    return () => ro.disconnect();
  }, [meet]);
  const { voor, hoofd, na } = extraDagenSegment({ startStr, endStr, isStart, isEnd, heeftVoor, heeftNa, ...meting });
  const totaal = voor + hoofd + na || 1;
  const streep = { background: `repeating-linear-gradient(135deg, ${bgColor} 0 5px, ${bgColor}55 5px 10px)`, color: txtColor,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78em', fontWeight: 800, letterSpacing: '0.02em', flexShrink: 0, minWidth: 22 };
  return (
    <span ref={ref} style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
      {voor > 0 && (
        <span style={{ ...streep, width: `${(voor / totaal) * 100}%`, borderRadius: hoofd || na ? '4px 0 0 4px' : '4px', boxShadow: `inset -2px 0 0 ${bgColor}` }}>−1</span>
      )}
      {hoofd > 0 && (
        <span style={{ flex: 1, minWidth: 0, backgroundColor: bgColor, ...(optie && { backgroundImage: OPTIE_ARCERING }), color: txtColor, padding: '3px 6px', overflow: 'hidden', whiteSpace: 'normal', fontSize: 'inherit', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {children}
        </span>
      )}
      {na > 0 && (
        <span style={{ ...streep, width: `${(na / totaal) * 100}%`, borderRadius: hoofd || voor ? '0 4px 4px 0' : '4px', boxShadow: `inset 2px 0 0 ${bgColor}` }}>+1</span>
      )}
    </span>
  );
}

// Persistent cache key
const DRAAIDAGEN_LOCALSTORAGE_KEY = 'draaidagenCache';

// Lees cache uit localStorage bij initialisatie
function getInitialDraaidagen() {
  try {
    const cached = localStorage.getItem(DRAAIDAGEN_LOCALSTORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && Array.isArray(parsed.data)) {
        return parsed.data;
      }
    }
  } catch (e) { console.warn('Failed to parse draaidagen cache from localStorage', e); }
  return [];
}

export const DraaidagenOverzicht = React.memo(function DraaidagenOverzicht() {
    const { selectedDraaidag, setSelectedDraaidag, fetchReservations, reservations, loading, error, initialDraaidagId, lastFetch } = useMaterialReservation();
    const [manualRefreshing, setManualRefreshing] = useState(false);
    const [, setTimeTick] = useState(0);

    // Keep the "X seconden geleden" label fresh
    useEffect(() => {
        const id = setInterval(() => setTimeTick(t => t + 1), 15000);
        return () => clearInterval(id);
    }, []);

    const formatLastFetch = useCallback((ts) => {
        if (!ts) return 'nooit';
        const sec = Math.max(0, Math.round((Date.now() - ts) / 1000));
        if (sec < 30) return 'zojuist';
        if (sec < 90) return '1 min geleden';
        if (sec < 3600) return `${Math.round(sec / 60)} min geleden`;
        const d = new Date(ts);
        return `om ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    }, []);

    const handleManualRefresh = useCallback(async () => {
        if (manualRefreshing) return;
        setManualRefreshing(true);
        try { await fetchReservations(true); } finally { setManualRefreshing(false); }
    }, [fetchReservations, manualRefreshing]);
    const [draaidagen, setDraaidagen] = useState(getInitialDraaidagen);
    // Weer + regenalert per komende draaidag (tool-feedback #46): alleen binnen ~9 dagen.
    const weerPerDraaidag = useDraaidagWeer(draaidagen);
    const [draaidagenCache, setDraaidagenCache] = useState({ data: getInitialDraaidagen(), timestamp: null });
    const [dataSource, setDataSource] = useState('cache');
    const DRAAIDAGEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache
    const [localLoading, setLocalLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDateEvents, setSelectedDateEvents] = useState([]);
    // Losse reserveringen (zonder draaidag)
    const [looseOpen, setLooseOpen] = useState(false);
    const [looseForm, setLooseForm] = useState({ name: '', start: '', end: '' });
    const startStandalone = useCallback(({ name, start, end, reservationId = null, moveTo = null }) => {
        setSelectedDraaidag({
            id: null, __standalone: true, reservationId,
            ...(moveTo ? { __moveTo: moveTo } : {}),
            fields: { Draaidag: (name || '').trim() || 'Losse reservering', StartDatum: start, EindDatum: end || start, Start: start, Eind: end || start },
        });
    }, [setSelectedDraaidag]);
    const [email, setEmail] = useState(null);
    const [legendaOpen, setLegendaOpen] = useState(false); // legenda dialog
    const [legendaAnchor, setLegendaAnchor] = useState(null); // legenda popover anchor
    const [externAnchor, setExternAnchor] = useState(null);   // popover "externe crew koppelen"
    const calendarRef = useRef(null);
    const accountFetchedRef = useRef(false);
    const [, setAccountCacheTick] = useState(0); // trigger re-render when accountCache fills

    // Pre-fetch account beeldmerken — runs once when draaidagen first load
    useEffect(() => {
      if (!draaidagen || draaidagen.length === 0 || accountFetchedRef.current) return;
      accountFetchedRef.current = true;
      const uniqueIds = [...new Set(draaidagen.map(d => d.fields?.AccountRecordId).filter(Boolean))];
      const toFetch = uniqueIds.filter(id => !accountCache[id]);
      if (toFetch.length === 0) return;

      // Batch: fetch all accounts in parallel (max 10 concurrent)
      const fetchBatch = async (ids) => {
        for (let i = 0; i < ids.length; i += 10) {
          const batch = ids.slice(i, i + 10);
          await Promise.allSettled(batch.map(id =>
            axios.get(`${BACKEND_URL}/Api/klanten/${id}`)
              .then(res => {
                const f = res.data?.account?.fields;
                if (!f) return;
                const beeldmerk = Array.isArray(f.Beeldmerk) && f.Beeldmerk[0];
                accountCache[id] = {
                  naam: f.Account || '',
                  beeldmerkUrl: beeldmerk?.thumbnails?.small?.url || beeldmerk?.url || null,
                };
              })
              .catch(() => {})
          ));
        }
      };
      fetchBatch(toFetch).then(() => setAccountCacheTick(t => t + 1));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draaidagen.length]);
    const [currentView, setCurrentView] = useState(() => {
      try { return sessionStorage.getItem('calendarView') || 'work_week'; } catch { return 'work_week'; }
    });

    // First load: today. After navigating back from draaidag: restore last position.
    const [currentDate, setCurrentDate] = useState(() => {
      try {
        const saved = sessionStorage.getItem('calendarDate');
        if (saved) { const d = new Date(saved); if (!isNaN(d.getTime())) return d; }
      } catch {}
      return new Date();
    }); 

    const [dubbeleReserveringenDialogOpen, setDubbeleReserveringenDialogOpen] = useState(false);
    const [dubbeleReserveringenData, setDubbeleReserveringenData] = useState({ draaidag: null, reserveringen: [] });

    const [draaidagenMetDubbeleReserveringen, setDraaidagenMetDubbeleReserveringen] = useState([]);
    const [toonAlertDubbeleReserveringen, setToonAlertDubbeleReserveringen] = useState(false);

    const { instance } = useMsal();

    const activeAccount = instance.getActiveAccount();

    // Koppel het ingelogde e-mailadres zodat "jouw draaidag" vs "andere draaidag" werkt
    useEffect(() => {
      const u = activeAccount?.username || null;
      setEmail(prev => (prev === u ? prev : u));
    }, [activeAccount]);

    // Fetch draaidagen from backend - alleen van 30 dagen geleden tot in de toekomst
    const fetchDraaidagenFromBackend = useCallback(async (startDateOverride) => {
      let cancelled = false;
      try {
        // Bepaal startdatum: 30 dagen geleden of override
        const today = new Date();
        const startDate = startDateOverride || new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        const startDateStr = startDate.toISOString().slice(0, 10);
        
        // Check cache first
        const now = Date.now();
        if (draaidagenCache.data && draaidagenCache.timestamp && 
            (now - draaidagenCache.timestamp) < DRAAIDAGEN_CACHE_DURATION) {
          setDraaidagen(draaidagenCache.data);
          setDataSource('cache (localStorage)');
          setLocalLoading(false);
          return;
        }
        
        // Stop existing timer if it exists (try-catch to avoid warnings)
        try {
          console.timeEnd('fetchDraaidagen');
        } catch (e) {
          // Timer doesn't exist, ignore
        }
        console.time('fetchDraaidagen');
        const response = await axios.get(`${BACKEND_URL}/Api/draaidagen?startDate=${startDateStr}`);
        if (!cancelled) {
          setDraaidagen(response.data);
          setDataSource(`PostgreSQL (${response.data?.length || 0} records, ${new Date().toLocaleTimeString('nl-NL')})`);
          setDraaidagenCache({ data: response.data, timestamp: now });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[FETCH] Error while fetching draaidagen from backend:', error);
        }
      } finally {
        if (!cancelled) {
          setLocalLoading(false);
        }
      }
      return () => { cancelled = true; };
    }, [draaidagenCache.timestamp]);

    // Memoize dubbele reserveringen identificatie - alleen voor relevante (toekomstige) draaidagen
    const identificeerDubbeleReserveringen = useCallback((reserveringen, draaidagenLijst) => {
      // Tel reserveringen per draaidag
      const draaidagCounts = reserveringen.reduce((acc, reservering) => {
        const draaidagIds = reservering.fields?.DraaidagOutlook;
        if (Array.isArray(draaidagIds)) {
          draaidagIds.forEach(draaidagId => {
            acc[draaidagId] = (acc[draaidagId] || 0) + 1;
          });
        }
        return acc;
      }, {});

      // Filter op draaidagen met meer dan 1 reservering
      const dubbeleDraaidagIds = Object.keys(draaidagCounts).filter(draaidagId => draaidagCounts[draaidagId] > 1);
      
      // Filter alleen draaidagen die in de toekomst liggen of vandaag zijn
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const relevanteDubbeleDraaidagen = dubbeleDraaidagIds.filter(draaidagId => {
        const draaidag = draaidagenLijst?.find(d => d.id === draaidagId);
        const startRaw = getDraaidagDateValue(draaidag?.fields, 'start');
        const startDatum = parseAirtableDate(startRaw);
        if (!startDatum) return false;
        startDatum.setHours(0, 0, 0, 0);
        
        // Alleen tonen als de draaidag vandaag of in de toekomst is
        return startDatum >= today;
      });
      
      return relevanteDubbeleDraaidagen;
    }, []);

    // Consolideer initiele data fetching
    // Trigger sync — delta (fast, only recent changes) or full
    const triggerSync = useCallback(async (mode = 'delta') => {
      try {
        await axios.post(`${BACKEND_URL}/Api/draaidagen/sync?mode=${mode}`);
        setDraaidagenCache({ data: null, timestamp: null });
      } catch (e) {
        console.warn('[Draaidagen] Sync failed (non-blocking):', e.message);
      }
    }, []);

    const initRef = useRef(false);
    useEffect(() => {
      if (initRef.current) return;
      initRef.current = true;
      let cancelled = false;

      const initializeData = async () => {
        // 1. Show cached data immediately
        await fetchDraaidagenFromBackend();
        // 2. Fetch reservations in parallel
        fetchReservations();
        // 3. Delta sync in background (fast — only recent changes)
        triggerSync('delta').then(() => {
          if (!cancelled) fetchDraaidagenFromBackend();
        });
      };

      initializeData();

      // Delta sync every 10 minutes, full sync every hour
      const deltaInterval = setInterval(() => {
        if (!cancelled) triggerSync('delta').then(() => fetchDraaidagenFromBackend());
      }, 10 * 60 * 1000);
      const fullInterval = setInterval(() => {
        if (!cancelled) triggerSync('full').then(() => fetchDraaidagenFromBackend());
      }, 60 * 60 * 1000);

      return () => { cancelled = true; clearInterval(deltaInterval); clearInterval(fullInterval); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // fetchReservations is already called in initializeData above

    // Check voor dubbele reserveringen wanneer beide data beschikbaar zijn
    useEffect(() => {
      if (draaidagen.length > 0 && reservations.length > 0) {
        const dubbeleDraaidagenIds = identificeerDubbeleReserveringen(reservations, draaidagen);
        setDraaidagenMetDubbeleReserveringen(dubbeleDraaidagenIds);
        setToonAlertDubbeleReserveringen(dubbeleDraaidagenIds.length > 0);
      }
    }, [draaidagen, reservations, identificeerDubbeleReserveringen]);

    // Auto-select draaidag from URL parameter (for "Open in new tab" support)
    useEffect(() => {
      if (initialDraaidagId && draaidagen.length > 0 && !selectedDraaidag) {
        const draaidag = draaidagen.find(d => d.id === initialDraaidagId);
        if (draaidag) {
          setSelectedDraaidag(draaidag);
        }
      }
    }, [initialDraaidagId, draaidagen, selectedDraaidag, setSelectedDraaidag]);

    const calculateDaysDifference = useCallback((start, end) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
  
      // Calculate difference in time
      const timeDifference = endDate.getTime() - startDate.getTime();
  
      // Calculate difference in days
      const daysDifference = timeDifference / (1000 * 3600 * 24) + 1; // Add 1 to include both start and end dates
  
      // Return appropriate string
      return daysDifference === 1 ? "1 dag" : `${daysDifference} dagen`;
    }, []);

    const getDraaidagDateValue = useCallback((fields, kind) => {
      if (!fields) return null;

      // If requesting end date: check if Outlook start/end are identical (PostgreSQL sync bug).
      // If so, prefer StartDatum/EindDatum (pure dates) which are correctly synced.
      if (kind === 'end') {
        const outlookStart = fields['StartdatumOutlook'] || fields['StartDatumOutlook'] || '';
        const outlookEnd = fields['EinddatumOutlook'] || fields['EindDatumOutlook'] || '';
        if (outlookStart && outlookEnd && outlookStart === outlookEnd) {
          // Outlook dates are identical — use pure date fields instead
          const pureDateEnd = fields['EindDatum'] || fields['Einddatum'];
          if (pureDateEnd) return pureDateEnd;
        }
      }

      const startKeys = [
        'StartdatumOutlook',
        'StartDatumOutlook',
        'startDatumOutlook',
        'StartDatum',
        'Startdatum'
      ];
      const endKeys = [
        'EinddatumOutlook',
        'EindDatumOutlook',
        'eindDatumOutlook',
        'EindDatum',
        'Einddatum'
      ];
      const keys = kind === 'start' ? startKeys : endKeys;
      for (const key of keys) {
        if (fields[key]) return fields[key];
      }
      return null;
    }, []);

    const parseAirtableDate = useCallback((value) => {
      if (!value) return null;
      const raw = String(value).trim();
      
      // Remove time component if present (e.g., "1/20/2026 12:00am" → "1/20/2026")
      const dateOnly = raw.split(' ')[0];

      let year, month, day;

      // If ISO datetime, convert UTC to local date (timestamps without Z are still UTC from Airtable/PostgreSQL)
      if (raw.includes('T')) {
        const utcStr = raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z';
        const localDate = new Date(utcStr);
        if (!isNaN(localDate.getTime())) {
          return new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 12, 0, 0, 0);
        }
        // Fallback: extract date part directly
        const datePart = raw.slice(0, 10);
        const [y, m, d] = datePart.split('-').map(Number);
        if (y && m && d) {
          year = y; month = m; day = d;
        }
      }
      // Date-only ISO "YYYY-MM-DD"
      else if (dateOnly.includes('-') && dateOnly.length >= 10) {
        const [y, m, d] = dateOnly.slice(0, 10).split('-').map(Number);
        if (y && m && d) {
          year = y; month = m; day = d;
        }
      }
      // Date with slashes: M/DD/YYYY (American - Airtable default)
      else if (dateOnly.includes('/')) {
        const parts = dateOnly.split('/');
        if (parts.length >= 3) {
          const p0 = Number(parts[0]);
          const p1 = Number(parts[1]);
          const p2 = Number(String(parts[2]).split(' ')[0]);
          
          if (p1 > 12) {
            // American: M/DD/YYYY (day > 12)
            month = p0; day = p1; year = p2;
          } else if (p0 > 12) {
            // European: DD/MM/YYYY (day > 12)
            day = p0; month = p1; year = p2;
          } else {
            // Ambiguous - assume American (Airtable default)
            month = p0; day = p1; year = p2;
          }
        }
      }

      // Create date at NOON to avoid any timezone edge cases
      // Using 12:00 ensures the date stays in the correct calendar day
      // regardless of how react-big-calendar interprets midnight times
      if (year && month && day && month <= 12 && day <= 31) {
        // Create JS Date at noon local time
        return new Date(year, month - 1, day, 12, 0, 0, 0);
      }

      // Fallback to moment parsing
      const fallback = moment(raw, ['YYYY-MM-DD', 'M/D/YYYY', 'D/M/YYYY', 'MM/DD/YYYY', 'DD/MM/YYYY']);
      if (fallback.isValid()) {
        const d = new Date(fallback.year(), fallback.month(), fallback.date(), 12, 0, 0, 0);
        return d;
      }
      return null;
    }, []);

    // Bepaal welke draaidagen een ExtraDagNa of ExtraDagVoor reservering hebben
    const draaidagenMetExtraDagen = useMemo(() => {
      const extraDagNaSet = new Set();
      const extraDagVoorSet = new Set();
      if (Array.isArray(reservations)) {
        reservations.forEach(res => {
          const draaidagId = res?.fields?.DraaidagOutlook?.[0];
          if (draaidagId) {
            if (res?.fields?.ExtraDagNa === true) {
              extraDagNaSet.add(draaidagId);
            }
            if (res?.fields?.ExtraDagVoor === true) {
              extraDagVoorSet.add(draaidagId);
            }
          }
        });
      }
      return { extraDagNa: extraDagNaSet, extraDagVoor: extraDagVoorSet };
    }, [reservations]);

    // Bereken reserveringen per draaidag lokaal (voor real-time kleur updates)
    const reservationCountByDraaidag = useMemo(() => {
      const counts = {};
      if (Array.isArray(reservations)) {
        reservations.forEach(res => {
          const draaidagIds = res?.fields?.DraaidagOutlook || [];
          draaidagIds.forEach(id => {
            counts[id] = (counts[id] || 0) + 1;
          });
        });
      }
      return counts;
    }, [reservations]);

    // Memoize events generation
    const events = useMemo(() => {
      const result = draaidagen.map((record, index) => {
        // Set a default style
        let style = {};

        // Check if logged-in user is in the camera operator emails
        // Support both old field name (EmailCameraman) and new Airtable field name
        const cameraEmails = record.fields.EmailCameraman
            || record.fields['Email Camera Operators (from Crew)']
            || [];
        const emailLc = (email || '').toLowerCase();
        const isUserCameramanForEvent = !!emailLc && Array.isArray(cameraEmails)
            && cameraEmails.some(e => (e || '').toLowerCase() === emailLc);

        // Use local reservation count instead of Airtable field for real-time updates
        // When a reservation is deleted, this count updates immediately
        const localReservationCount = reservationCountByDraaidag[record.id] || 0;
        const hasReservation = localReservationCount > 0 || record.fields['Geen Materiaal Nodig'];

        // Kleur = reserveringsstatus (één set, zie DraaidagPil). "Jouw draaidag" is geen
        // tweede kleurenset meer maar een rand links — rustiger (wiki-feedback 30 sep).
        const pilStatus = draaidagenMetDubbeleReserveringen.includes(record.id) ? 'Dubbel'
          : hasReservation ? 'Gereserveerd' : 'Open';
        style = pilKleuren(pilStatus, { jouw: isUserCameramanForEvent });

        // Maak een leesbare korte titel voor de kalender
        // WeergaveNaam: check reservering eerst (daar wordt het opgeslagen), dan draaidag veld
        const matchingRes = reservations?.find(r => (r?.fields?.IDdraaidagOutlook || []).includes(record.id));
        const customNaam = matchingRes?.fields?.WeergaveNaam || record.fields.WeergaveNaamMateriaalApp;
        const originalTitle = record.fields.Draaidag || '';
        const fullTitle = customNaam || originalTitle;

        // Crew parsing (altijd, voor tooltip)
        const isName = (v) => typeof v === 'string' && !v.startsWith('rec');
        const crewLookup = record.fields['Naam (from Crew)'];
        const crewField = record.fields.Camerapersonen;
        const crewFromLookup = Array.isArray(crewLookup) && crewLookup.every(isName) ? crewLookup.join(' + ') : '';
        const crewFromField = Array.isArray(crewField) && crewField.every(isName) ? crewField.join(' + ') : (typeof crewField === 'string' ? crewField : '');
        // Gekoppelde crew + namen uit de titel die niet gekoppeld zijn (externe crew met "(b)"/"(g)" staat
        // alleen in de Outlook-titel; de Airtable-automation koppelt alleen genodigden — Robin, 22 sep 2026)
        const crewSamen = crewSamengevoegd(crewFromLookup ? crewLookup : (crewFromField ? crewField : []), originalTitle);
        const crewNaam = crewSamen.label;
        const crewNietGekoppeld = crewSamen.nietGekoppeld;

        // Als er een custom weergavenaam is, gebruik die direct
        let shortTitle = '';
        if (customNaam && customNaam.trim()) {
          shortTitle = customNaam;
        } else {
          // Structurele parsing: altijd uit de Draaidag titel
          // Format: "Namen || Projectnaam - Extra info - [nummer] - INITIALEN PL"
          // Soms: "Namen || KLANT + DRAAIDAG - [nummer] - INITIALEN"
          // Soms zonder ||: "Projectnaam - [nummer]"

          // 1. Extract crew from BEFORE '||' (always in title, most reliable source)
          const pipeIdx = originalTitle.indexOf('||');
          let crewFromTitle = '';
          let projectPart = originalTitle;

          if (pipeIdx > 0) {
            crewFromTitle = originalTitle.substring(0, pipeIdx).trim();
            projectPart = originalTitle.substring(pipeIdx + 2).trim();
          }

          // 2. Crew already parsed above (crewNaam)

          // 3. Extract project name and number from projectPart
          // Remove trailing " - XX" (PL initials)
          // PL-initialen achteraan ("- LJ") blijven zichtbaar als "(LJ)" (Joris, 22 sep 2026)
          const plInitialen = projectPart.match(/(?:^|\s)-\s*([A-Z][A-Za-z]{1,4})\s*\??\s*$/)?.[1] || '';
          const projectClean = projectPart.replace(/(?:^|\s)-\s*[A-Z][A-Za-z]{1,4}\s*\??\s*$/, '').trim();
          const projectNumMatch = projectClean.match(/\[(\d+)\]/)?.[1] || '';
          // Remove [number] and trailing dashes to get clean project name
          const projectName = projectClean.replace(/\s*-?\s*\[\d+\]\s*/, '').replace(/\s*-\s*$/, '').trim();

          // 4. Build short title: "Crew: Project - [nummer]"
          const parts = [];
          if (crewNaam) parts.push(crewNaam);
          if (projectName) {
            const projStr = projectNumMatch ? `${projectName} - [${projectNumMatch}]` : projectName;
            parts.push(projStr);
          }
          shortTitle = parts.join(': ') || fullTitle;
          if (plInitialen && parts.length) shortTitle += ` - ${plInitialen}`;
        }
        
        // Voor tooltip: extraheer altijd projectnummer uit originele titel
        const projectNumMatch = originalTitle.match(/\[(\d+)\]/)?.[1] || '';
        
        // Voeg indicator VOORAAN toe voor extra dagen (visueel belangrijk)
        const heeftExtraDagVoor = draaidagenMetExtraDagen.extraDagVoor.has(record.id);
        const heeftExtraDagNa = draaidagenMetExtraDagen.extraDagNa.has(record.id);
        
        // Extra dag indicators zijn nu visueel zichtbaar via de gestreepte balk-extensies
        
        // Structured tooltip data
        const klantAccountId = record.fields.AccountRecordId || null;
        const klantNaam = originalTitle.match(/\|\|\s*([^+\-\[]+)/)?.[1]?.trim() ||
                          originalTitle.split('/').pop()?.match(/^\s*([^-\[]+)/)?.[1]?.trim() || '';
        // Reservation status for tooltip
        const isDubbel = draaidagenMetDubbeleReserveringen.includes(record.id);
        const isGeenNodig = record.fields['Geen Materiaal Nodig'];
        const resStatus = isDubbel ? 'Dubbel' : isGeenNodig ? 'Niet nodig' : hasReservation ? 'Gereserveerd' : 'Open';

        // Hiërarchie (feedback Joris 30 sep): hoofdregel wat/waar + kleine meta-regel
        // (crew · #nummer · PL); OPTIE/LOCATIEBEZOEK als label. Projectkoppeling wint.
        const omschrijving = draaidagOmschrijving({
          titel: originalTitle,
          weergaveNaam: customNaam,
          projectnaam: record.fields['Projectnaam (from Project)'],
          projectnummer: record.fields['Projectnummer (from Project)'],
          crew: crewFromLookup ? crewLookup : (crewFromField ? crewField : []),
        });
        const tooltipData = {
          title: customNaam || shortTitle,
          hoofd: omschrijving.hoofd,
          labels: omschrijving.labels,
          meta: omschrijving.meta,
          projectNum: omschrijving.projectnummer || projectNumMatch,
          // open rollen ook zichtbaar zonder te koppelen externe crew (Codex r1)
          crew: crewSamen.openRollen.length ? `${crewNaam}${crewNaam ? ' · ' : ''}nog in te vullen: ${crewSamen.openRollen.join(', ')}` : crewNaam,
          crewNietGekoppeld: crewNietGekoppeld.length ? crewNietGekoppeld.map((x) => `${x.naam}${x.status ? ` (${x.status})` : ''}`).join(', ') : null,
          klantNaam,
          klantAccountId,
          materiaalCount: record.fields.Aantal || 0,
          heeftExtraDagVoor,
          heeftExtraDagNa,
          resStatus,
        };
        const tooltipText = JSON.stringify(tooltipData);
  
        const startRaw = getDraaidagDateValue(record.fields, 'start');
        const endRaw = getDraaidagDateValue(record.fields, 'end');

        // Parse dates for sorting/drilldown (Date objects at noon local)
        const startDate = parseAirtableDate(startRaw) || new Date();
        let endDate = parseAirtableDate(endRaw);
        if (!endDate) {
          endDate = new Date(startDate.getTime());
        }

        // For FullCalendar: extract YYYY-MM-DD from raw Airtable value
        // IMPORTANT: Airtable StartdatumOutlook is UTC datetime (e.g. "2026-02-25T23:00:00.000Z")
        // which is actually "2026-02-26 00:00 CET". We must convert to LOCAL date, not extract UTC date.
        const extractDateStr = (raw) => {
          if (!raw) return null;
          const s = String(raw).trim();
          // If it contains 'T' it's a UTC datetime — convert to local date
          // IMPORTANT: Airtable/PostgreSQL timestamps may lack 'Z' suffix but ARE UTC.
          // Without 'Z', JavaScript parses as local time → wrong date near midnight.
          if (s.includes('T')) {
            const utcStr = s.endsWith('Z') || s.includes('+') || s.includes('-', 11) ? s : s + 'Z';
            const d = new Date(utcStr); // Now correctly treated as UTC → converted to local
            if (!isNaN(d.getTime())) {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${dd}`;
            }
          }
          // Pure date: "2026-03-02" — use as-is
          const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})$/);
          if (isoMatch) return isoMatch[1];
          // Partial ISO: "2026-03-02 ..." (without T)
          const isoPartial = s.match(/^(\d{4}-\d{2}-\d{2})\s/);
          if (isoPartial) return isoPartial[1];
          // American format: "3/2/2026" or "03/02/2026"
          const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
          if (usMatch) {
            const m = usMatch[1].padStart(2, '0');
            const d = usMatch[2].padStart(2, '0');
            return `${usMatch[3]}-${m}-${d}`;
          }
          return null;
        };

        const addOneDay = (dateStr) => {
          if (!dateStr) return null;
          const d = new Date(dateStr + 'T12:00:00'); // noon to avoid tz shift
          d.setDate(d.getDate() + 1);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${dd}`;
        };

        const startStr = extractDateStr(startRaw) || startDate.toISOString().slice(0, 10);
        const endRawStr = extractDateStr(endRaw) || startStr;
        // FullCalendar exclusive end: add +1 day
        const endStr = addOneDay(endRawStr);

        return {
          title: shortTitle,
          omschrijving,
          fullTitle: fullTitle,
          originalTitle: originalTitle,
          tooltipText: tooltipText,
          start: startDate,  // Date object for sorting/drilldown
          end: endDate,      // Date object for sorting/drilldown
          startStr,          // YYYY-MM-DD for FullCalendar
          endStr,            // YYYY-MM-DD for FullCalendar (exclusive end)
          style: style,
          allDay: true,
          record: record,
          // externe crew die alleen in de Outlook-titel staat en nog in het Airtable Crew-veld gekoppeld moet worden
          crewNietGekoppeld,
          // rollen in de titel zonder naam ("+ Geluid + Visagie") — open plekken, geen crew om te koppelen
          openRollen: crewSamen.openRollen,
          airtableUrl: record.fields['Open draaidag']?.url || null,
          projectNum: omschrijving.projectnummer || projectNumMatch,
          eindStr: endRawStr,
        };
      });

      // Losse dag binnen een meerdaagse draaidag van hetzelfde project → ⚠ (alleen markeren, niets verbergen)
      const dubbel = mogelijkDubbeleDraaidagen(result.map((e) => ({ id: e.record?.id, projectnummer: e.projectNum, start: e.startStr, eind: e.eindStr })));
      result.forEach((e) => {
        e.mogelijkDubbel = dubbel.has(e.record?.id);
        if (e.mogelijkDubbel) {
          try { e.tooltipText = JSON.stringify({ ...JSON.parse(e.tooltipText), mogelijkDubbel: true }); } catch { /* tooltip blijft zoals hij was */ }
        }
      });
      
      // Losse reserveringen (zonder draaidag) → gele events in het overzicht.
      // Bron = de globale reservations-array (ververst ná elke save), gefilterd op
      // reserveringen zonder draaidag-koppeling. Robuuster dan een aparte fetch.
      const toDateStr = (v) => { const m = v && String(v).match(/^(\d{4}-\d{2}-\d{2})/); return m ? m[1] : null; };
      const addOneDayStr = (ds) => { const d = new Date(ds + 'T12:00:00'); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
      const looseReservations = LOSSE_RESERVERINGEN_ENABLED ? (Array.isArray(reservations) ? reservations : []).filter(r => {
        const dd = r?.fields?.IDdraaidagOutlook || r?.fields?.DraaidagOutlook || r?.fields?.Draaidag || [];
        return !(Array.isArray(dd) ? dd.length : dd);
      }) : [];
      looseReservations.forEach(r => {
        const f = r.fields || r;
        // Skip lege orphan-reserveringen (geen materiaal) → voorkomt clutter van oude data
        const matCount = Array.isArray(f.Materiaal) ? f.Materiaal.length : 0;
        if (matCount === 0) return;
        const rawName = Array.isArray(f.DraaidagNaam) ? f.DraaidagNaam[0] : f.DraaidagNaam;
        const naam = (rawName && String(rawName).trim()) || 'Losse reservering';
        const startStr = toDateStr(f.Start || f.start_date);
        if (!startStr) return;
        const endRawStr = toDateStr(f.Eind || f.end_date) || startStr;
        const startDate = new Date(startStr + 'T12:00:00');
        const endDate = new Date(endRawStr + 'T12:00:00');
        const tooltipData = { title: naam, originalTitle: null, projectNum: '', crew: '', klantNaam: '', klantAccountId: null, materiaalCount: matCount, heeftExtraDagVoor: false, heeftExtraDagNa: false, resStatus: 'Losse reservering' };
        result.push({
          title: naam,
          fullTitle: naam,
          originalTitle: '',
          tooltipText: JSON.stringify(tooltipData),
          start: startDate,
          end: endDate,
          startStr,
          endStr: addOneDayStr(endRawStr),
          style: pilKleuren('Losse reservering'),
          omschrijving: { hoofd: naam, labels: [], meta: '', optie: false },
          allDay: true,
          record: null,
          isLoose: true,
          looseRes: { id: r.id, name: naam, start: startStr, end: endRawStr },
        });
      });

      // Sorteer events: multi-day events eerst (voor betere weergave in month view)
      // Multi-day events worden bovenaan getoond
      result.sort((a, b) => {
        const aDuration = a.end.getTime() - a.start.getTime();
        const bDuration = b.end.getTime() - b.start.getTime();
        const oneDay = 24 * 60 * 60 * 1000;
        
        const aIsMultiDay = aDuration > oneDay;
        const bIsMultiDay = bDuration > oneDay;
        
        // Multi-day events eerst
        if (aIsMultiDay && !bIsMultiDay) return -1;
        if (!aIsMultiDay && bIsMultiDay) return 1;
        
        // Bij gelijke type: langere events eerst
        return bDuration - aDuration;
      });
      
      return result;
    }, [draaidagen, email, reservations, draaidagenMetDubbeleReserveringen, draaidagenMetExtraDagen, reservationCountByDraaidag]);
    
    // Memoize handlers
    const handleDrillDown = useCallback((date) => {
      // Filter events that overlap with the clicked date
      // For multi-day events, we need to check if the date falls WITHIN the event range
      const clickedDate = new Date(date);
      clickedDate.setHours(0, 0, 0, 0);
      
      const eventsOnDate = events.filter(event => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        eventStart.setHours(0, 0, 0, 0);
        eventEnd.setHours(0, 0, 0, 0);
        
        // Check if clicked date falls within event range
        // Note: eventEnd is EXCLUSIVE (already has +1 day added for react-big-calendar)
        return clickedDate >= eventStart && clickedDate < eventEnd;
      });
      
      // Sorteer popup: single-day events eerst, dan multi-day
      // Dit is het omgekeerde van de month view sortering
      const oneDay = 24 * 60 * 60 * 1000;
      eventsOnDate.sort((a, b) => {
        const aDuration = a.end.getTime() - a.start.getTime();
        const bDuration = b.end.getTime() - b.start.getTime();
        
        const aIsMultiDay = aDuration > oneDay;
        const bIsMultiDay = bDuration > oneDay;
        
        // Single-day events eerst in popup
        if (!aIsMultiDay && bIsMultiDay) return -1;
        if (aIsMultiDay && !bIsMultiDay) return 1;
        
        // Bij gelijke type: sorteer op titel
        return (a.title || '').localeCompare(b.title || '');
      });
    
      setSelectedDateEvents(eventsOnDate);
      setDialogOpen(true);
    }, [events]);

    const handleViewChange = useCallback((view) => {
      setCurrentView(view);
    }, []);

    // Onthoud de huidige datum wanneer de gebruiker navigeert
    const handleNavigate = useCallback((newDate) => {
      setCurrentDate(newDate);
      try {
        localStorage.setItem('calendarCurrentDate', newDate.toISOString());
      } catch (e) { /* ignore */ }
    }, []);

    const handleMismatchedReservationClick = useCallback(async (reservation) => {

      // Guard: check if reservation is an object with fields (not just an ID string)
      if (!reservation?.fields) {
        return;
      }

      if (reservation.fields.DraaidagOutlook?.[0]) {
        const draaidag = draaidagen.find(draaidag => draaidag.id === reservation.fields.DraaidagOutlook[0]);
        setSelectedDraaidag(draaidag);
      }
    }, [draaidagen, setSelectedDraaidag]);

    const handleDraaidagClick = useCallback(async(draaidag) => {
      // Check of deze draaidag dubbele reserveringen heeft
      if (draaidagenMetDubbeleReserveringen.includes(draaidag.id)) {
        const matchingRes = reservations.filter(r => {
          const ids = r?.fields?.IDdraaidagOutlook || r?.fields?.DraaidagOutlook || [];
          return ids.includes(draaidag.id);
        });
        if (matchingRes.length > 1) {
          setDubbeleReserveringenData({ draaidag, reserveringen: matchingRes });
          setDubbeleReserveringenDialogOpen(true);
          return;
        }
      }
      setSelectedDraaidag(draaidag);
    }, [setSelectedDraaidag, draaidagenMetDubbeleReserveringen, reservations]);

    const handleCloseDialog = useCallback(() => setDialogOpen(false), []);
    // Komende draaidagen waar externe crew alleen in de Outlook-titel staat. Externe crew wordt bewust niet in
    // Outlook uitgenodigd (die krijgen anders bij elke wijziging een mail), dus koppelen gebeurt handmatig in Airtable.
    const externeKoppelingen = useMemo(() => {
      const vandaag = formatDateISO(new Date());
      return (events || [])
        .filter((e) => e.record && e.crewNietGekoppeld?.length && (e.startStr || '') >= vandaag)
        .sort((a, b) => (a.startStr || '').localeCompare(b.startStr || ''));
    }, [events]);

    const handleCloseLegenda = useCallback(() => setLegendaOpen(false), []);
    const handleOpenLegenda = useCallback(() => setLegendaOpen(true), []);
    const handleCloseDubbeleReserveringen = useCallback(() => setDubbeleReserveringenDialogOpen(false), []);

    // Delete handler for mismatched reservations
    const handleDeleteMismatchedReservation = useCallback(async (reservationId) => {
      try {
        await axios.delete(API_ENDPOINTS.reservations.delete(reservationId));
        // Refresh reservations after delete
        fetchReservations(true);
        toast.success("Reservering succesvol verwijderd");
      } catch (error) {
        console.error("Error deleting reservation:", error);
        toast.error("Er is een fout opgetreden bij het verwijderen van de reservering");
      }
    }, [fetchReservations]);
    
    // Verrijk reservations met draaidagdata voor MismatchedReservations
    const reservationsWithDraaidagData = useMemo(() => {
      if (!Array.isArray(reservations) || !Array.isArray(draaidagen)) return reservations;
      return reservations.map(res => {
        const draaidagId = res.fields.DraaidagOutlook?.[0];
        const draaidag = draaidagId ? draaidagen.find(d => d.id === draaidagId) : null;
        return {
          ...res,
          fields: {
            ...res.fields,
            OrigineleDraaidagNaam: draaidag?.fields?.Draaidag || undefined,
            startDatumDraaidag: draaidag?.fields?.StartDatum || undefined,
            eindDatumDraaidag: draaidag?.fields?.EindDatum || undefined,
          }
        };
      });
    }, [reservations, draaidagen]);

    // Lokale update van een draaidag (voor snelle UI)
    const updateDraaidagLocally = useCallback((updatedDraaidag) => {
      setDraaidagen(prev =>
        prev.map(d => d.id === updatedDraaidag.id ? { ...d, fields: { ...d.fields, ...updatedDraaidag.fields } } : d)
      );
    }, []);

    useEffect(() => {
      if (draaidagenCache.data && draaidagen.length === 0) {
        setDraaidagen(draaidagenCache.data);
      }
    }, [draaidagenCache.data]);

    // Sla cache op in localStorage bij elke update
    useEffect(() => {
      if (draaidagenCache.data) {
        try {
          localStorage.setItem(DRAAIDAGEN_LOCALSTORAGE_KEY, JSON.stringify(draaidagenCache));
        } catch (e) { console.warn('Failed to save draaidagen cache to localStorage', e); }
      }
    }, [draaidagenCache]);

    // ── Werkbalk (zelfde lijn als de andere planningstabs) ──────────────────────
    const wisselWeergave = useCallback((key) => {
      setCurrentView(key);
      try { sessionStorage.setItem('calendarView', key); } catch { /* privémodus */ }
      const api = calendarRef.current?.getApi();
      if (api && key !== 'agenda') api.changeView(key === 'work_week' ? 'twoWeek' : 'dayGridMonth');
    }, []);

    // Eén navigatie voor kalender én lijst: de kalender zet via datesSet zelf currentDate.
    const gaNaar = useCallback((datum) => {
      const api = calendarRef.current?.getApi();
      // Kalender: datesSet zet daarna zelf currentDate (en de sessionStorage).
      if (api && currentView !== 'agenda') api.gotoDate(datum);
      else setCurrentDate(datum);
    }, [currentView]);

    // Stille samenvatting links: hoeveel draaidagen in beeld en hoeveel nog zonder materiaal.
    // Maand en Lijst tellen de kalendermaand ("in september"), 2 weken het zichtbare venster
    // inclusief de −1/+1-dagen (zoals de kalender ze tekent).
    const telling = useMemo(() => {
      const d = new Date(currentDate);
      const dagErbij = (iso, n) => { const x = new Date(`${iso}T12:00:00`); x.setDate(x.getDate() + n); return formatDateISO(x); };
      let selectie;
      if (currentView === 'work_week') {
        const van = formatDateISO(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
        const tot = dagErbij(van, 14);
        selectie = (e) => {
          const id = e.record?.id;
          const start = draaidagenMetExtraDagen.extraDagVoor.has(id) ? dagErbij(e.startStr, -1) : e.startStr;
          const eind = draaidagenMetExtraDagen.extraDagNa.has(id) ? dagErbij(e.endStr, 1) : e.endStr;
          return start < tot && eind > van; // start inclusief, eind exclusief (FullCalendar)
        };
      } else if (currentView === 'agenda') {
        // zelfde selectie als de lijst zelf: start in de maand
        selectie = (e) => e.start && e.start.getMonth() === d.getMonth() && e.start.getFullYear() === d.getFullYear();
      } else {
        const van = formatDateISO(new Date(d.getFullYear(), d.getMonth(), 1));
        const tot = formatDateISO(new Date(d.getFullYear(), d.getMonth() + 1, 1));
        selectie = (e) => e.startStr < tot && e.endStr > van;
      }
      const inBeeld = events.filter((e) => !e.isLoose && selectie(e));
      const open = inBeeld.filter((e) => { try { return JSON.parse(e.tooltipText).resStatus === 'Open'; } catch { return false; } }).length;
      return { n: inBeeld.length, open };
    }, [events, currentDate, currentView, draaidagenMetExtraDagen]);
    const waar = currentView === 'work_week' ? 'in beeld' : `in ${currentDate.toLocaleDateString('nl-NL', { month: 'long' })}`;
    const samenvatting = `${telling.n} draaidag${telling.n === 1 ? '' : 'en'} ${waar} · ${telling.open} nog zonder materiaal · bijgewerkt ${formatLastFetch(lastFetch)}`;

    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ pb: 8 }}>
        <Grid item xs={12}>
          {/* "Materialen" staat als tab in de kopbalk (PlanningTabs, 26 sep). Werkbalk in
              dezelfde lijn als Rooster/Capaciteit/Draaidagen (wiki-feedback Joris, 30 sep):
              links een stille samenvatting, rechts weergave · periode · Vandaag · verversen ·
              de ene primaire actie. */}
          {!selectedDraaidag && (
          <PageHeader
            kaal
            title={samenvatting}
            rightActions={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {(loading || manualRefreshing) && (
                  <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={10} thickness={6} sx={{ color: '#94a3b8' }} />
                    <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>bijwerken…</Typography>
                  </Box>
                )}
                <ToggleButtonGroup size="small" exclusive value={currentView} onChange={(_e, v) => v && wisselWeergave(v)}
                  aria-label="Weergave"
                  sx={{ bgcolor: '#fff', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.1, py: 0.35, fontSize: 12, fontWeight: 700 } }}>
                  <ToggleButton value="month">Maand</ToggleButton>
                  <ToggleButton value="work_week">2 weken</ToggleButton>
                  <ToggleButton value="agenda">Lijst</ToggleButton>
                </ToggleButtonGroup>
                <WeekSelector
                  weekStart={currentDate}
                  onChange={gaNaar}
                  eenheid={currentView === 'work_week' ? 'week' : 'maand'}
                  rangeWeeks={2}
                  minWidth={150}
                />
                <Button size="small" variant="outlined" onClick={() => gaNaar(new Date())}
                  sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 1.25, py: 0.3, borderColor: '#cbd5e1', color: '#334155' }}>
                  Vandaag
                </Button>
                <Tooltip title={`Vernieuwen · bijgewerkt ${formatLastFetch(lastFetch)}`}>
                  <span>
                    <IconButton size="small" onClick={handleManualRefresh} disabled={manualRefreshing} aria-label="Vernieuwen"><RefreshIcon /></IconButton>
                  </span>
                </Tooltip>
                {LOSSE_RESERVERINGEN_ENABLED && (
                  <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />}
                    onClick={() => { setLooseForm({ name: '', start: formatDateISO(new Date()), end: formatDateISO(new Date()) }); setLooseOpen(true); }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Losse reservering
                  </Button>
                )}
              </Box>
            )}
          />
          )}

          {/* Losse reservering aanmaken */}
          <Dialog open={looseOpen} onClose={() => setLooseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <SIcon name="add_box" size={22} sx={{ color: T.primary }} />
                <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>Losse reservering</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, mb: 2, borderRadius: '0.6rem', bgcolor: '#fef3c7', border: '1px solid #f59e0b40' }}>
                <SIcon name="info" size={16} sx={{ color: '#d4943a', marginTop: 1 }} />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: '#7a5b1e', lineHeight: 1.5 }}>
                  Dit is <strong>geen draaidag</strong> — komt niet in Airtable of agenda's. Het blokkeert wel materiaal voor de gekozen periode.
                </Typography>
              </Box>
              <TextField autoFocus size="small" fullWidth label="Naam (bv. 'Uitleen Jan')" value={looseForm.name} onChange={(e) => setLooseForm(p => ({ ...p, name: e.target.value }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField size="small" fullWidth type="date" label="Van" InputLabelProps={{ shrink: true }} value={looseForm.start} onChange={(e) => setLooseForm(p => ({ ...p, start: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
                <TextField size="small" fullWidth type="date" label="Tot en met" InputLabelProps={{ shrink: true }} value={looseForm.end} onChange={(e) => setLooseForm(p => ({ ...p, end: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
                <Box onClick={() => setLooseOpen(false)} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
                <Box onClick={() => { if (!looseForm.start) { toast.error('Kies een datum'); return; } setLooseOpen(false); startStandalone(looseForm); }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', background: `linear-gradient(135deg, #635983 0%, #526075 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem' }}>
                  <SIcon name="arrow_forward" size={16} sx={{ color: '#fff' }} /> Materiaal kiezen
                </Box>
              </Box>
            </Box>
          </Dialog>

          {/* Data source indicator removed */}
          {toonAlertDubbeleReserveringen && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mt: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}25` }}>
              <SIcon name="warning" size={18} sx={{ color: T.error, marginTop: 2 }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.error, lineHeight: 1.6 }}>
                <strong>Let op:</strong> Er zijn draaidagen met dubbele reserveringen (rood gemarkeerd). Corrigeer deze door de draaidag te selecteren en de onjuiste reservering te verwijderen.{' '}
                <a href='https://airtable.com/apphMT5j6gtUnMY1p/pagWOK6C02DDm7YCt' target="_blank" rel="noopener noreferrer" style={{ color: T.primary }}>Bekijk in Airtable</a>
              </Typography>
            </Box>
          )}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '1rem', overflow: 'hidden' } }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${T.surfaceLow}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SIcon name="event" size={20} sx={{ color: T.primary }} />
                <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 800, color: T.text }}>
                  {selectedDateEvents[0] && moment(selectedDateEvents[0].start).format('dddd D MMMM YYYY')}
                </Typography>
              </Box>
              <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: T.primary, bgcolor: T.primaryContainer, px: 1.25, py: 0.3, borderRadius: '9999px' }}>
                {selectedDateEvents.length} {selectedDateEvents.length === 1 ? 'draaidag' : 'draaidagen'}
              </Typography>
            </Box>
            <DialogContent sx={{ pt: 1.5 }}>
              <List disablePadding>
                {selectedDateEvents.map((event, index) => {
                  const record = event.record;
                  const fields = record?.fields || {};
                  const matchingReservation = reservations?.find(r => r?.fields?.IDdraaidagOutlook?.[0] === record?.id);
                  const hasReservation = !!matchingReservation;
                  const hasMaterials = hasReservation && Array.isArray(matchingReservation?.fields?.Materiaal) && matchingReservation.fields.Materiaal.length > 0;
                  const isGeenMateriaalNodig = hasReservation && !hasMaterials;
                  // Camerapersonen kan een string of array zijn (Airtable linked records)
                  const camerapersonen = Array.isArray(fields.Camerapersonen) 
                    ? fields.Camerapersonen.join(' ') 
                    : (fields.Camerapersonen || '');
                  const isMyDraaidag = camerapersonen.toLowerCase().includes(email?.split('@')[0]?.toLowerCase() || '');
                  
                  // Gebruik event.title (= shortTitle uit kalender parsing, inclusief weergavenaam)
                  const displayTitle = event.title || fields.Draaidag || '';
                  const origTitle = fields.Draaidag || '';
                  const hasCustomName = displayTitle !== origTitle && origTitle;
                  const projectNum = origTitle.match(/\[(\d+)\]/)?.[1] || '';
                  // Crew uit lookup velden (zelfde als kalender)
                  const isName = (v) => typeof v === 'string' && !v.startsWith('rec');
                  const crewLookup = fields['Naam (from Crew)'];
                  const crewField = fields.Camerapersonen;
                  const crewMatch = crewSamengevoegd(
                    (Array.isArray(crewLookup) && crewLookup.every(isName)) ? crewLookup
                      : (Array.isArray(crewField) && crewField.every(isName)) ? crewField
                        : (typeof crewField === 'string' ? [crewField] : []),
                    fields.Draaidag,
                  ).label;
                  // PL meteen in het overzicht, niet pas na doorklikken (RVE, 1 sep).
                  const plNaam = Array.isArray(fields.PL) ? fields.PL.filter(isName).join(', ') : (isName(fields.PL) ? fields.PL : '');

                  return (
                    <ListItem 
                      key={record.id}
                      button 
                      onClick={() => {
                        handleCloseDialog();
                        handleDraaidagClick(record);
                      }}
                      sx={{
                        bgcolor: event.style?.backgroundColor ? `${event.style.backgroundColor}18` : T.surface,
                        borderRadius: '0.75rem',
                        mb: 1,
                        border: `1px solid ${event.style?.backgroundColor ? `${event.style.backgroundColor}35` : T.outlineVariant + '30'}`,
                        borderLeft: `3px solid ${event.style?.backgroundColor || T.outlineVariant}`,
                        px: 2, py: 1.5,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: event.style?.backgroundColor ? `${event.style.backgroundColor}28` : T.surfaceLow,
                          borderColor: T.primary,
                        }
                      }}
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 700, color: T.text }}>
                                {displayTitle}
                              </Typography>
                              {hasCustomName && (
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {origTitle}
                                </Typography>
                              )}
                              {plNaam && (
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.secondary }}>PL {plNaam}</Typography>
                              )}
                            </Box>
                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              {isGeenMateriaalNodig ? (
                                <>
                                  <SIcon name="check_circle" size={14} sx={{ color: T.emerald }} />
                                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.emerald }}>Niet nodig</Typography>
                                </>
                              ) : hasReservation ? (
                                <>
                                  <SIcon name="check_circle" size={14} sx={{ color: T.emerald }} />
                                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.emerald }}>Gereserveerd</Typography>
                                </>
                              ) : (
                                <>
                                  <SIcon name="error_outline" size={14} sx={{ color: T.amber }} />
                                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.amber }}>Open</Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            {crewMatch && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SIcon name="groups" size={14} sx={{ color: T.outlineVariant }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>{crewMatch}</Typography>
                              </Box>
                            )}
                            {hasMaterials && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SIcon name="inventory_2" size={14} sx={{ color: T.outlineVariant }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>{matchingReservation.fields.Materiaal.length} materialen</Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>

            <DialogActions sx={{ borderTop: `1px solid ${T.surfaceLow}`, px: 3, py: 2 }}>
              <Box onClick={handleCloseDialog} sx={{ px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.secondary, border: `1.5px solid ${T.outlineVariant}40`, '&:hover': { bgcolor: T.surfaceLow } }}>
                Sluiten
              </Box>
            </DialogActions>
          </Dialog>

          {/* Old legenda dialog + floating button removed — replaced by inline Popover */}
          <DubbeleReserveringenDialog
            open={dubbeleReserveringenDialogOpen}
            onClose={handleCloseDubbeleReserveringen}
            draaidag={dubbeleReserveringenData.draaidag}
            reserveringen={dubbeleReserveringenData.reserveringen}
            alleMaterialen={[]}
            onKeep={async (keepId, deleteId) => {
              try {
                await axios.delete(API_ENDPOINTS.reservations.delete(deleteId));
                await fetchReservations(true);
                toast.success('Dubbele reservering opgelost');
                setDubbeleReserveringenDialogOpen(false);
                setSelectedDraaidag(dubbeleReserveringenData.draaidag);
              } catch (err) {
                toast.error('Fout bij verwijderen: ' + err.message);
              }
            }}
          />
          <MismatchedReservations 
            draaidag={draaidagen} 
            handleDeleteReservation={handleDeleteMismatchedReservation}
            reservationsData={reservationsWithDraaidagData}
            onReservationClick={handleMismatchedReservationClick}
            setGeselecteerdeDraaidag={setSelectedDraaidag}
          />
          {/* Tweede regel, zoals de filterregel op Draaidagen: legenda en wat er nog te doen is */}
          {!selectedDraaidag && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Button size="small" variant="text" onClick={(e) => setLegendaAnchor(legendaAnchor ? null : e.currentTarget)}
                startIcon={<SIcon name="palette" size={16} />}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#556071', fontSize: 12.5 }}>
                Legenda
              </Button>
              {externeKoppelingen.length > 0 && (
                <>
                  <Tooltip title="Externe crew staat in de Outlook-titel maar is niet gekoppeld in Airtable" arrow>
                    <Button size="small" variant="outlined" color="warning" onClick={(e) => setExternAnchor(externAnchor ? null : e.currentTarget)}
                      startIcon={<SIcon name="person_add" size={16} />}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, py: 0.2 }}>
                      Externe crew koppelen ({externeKoppelingen.length})
                    </Button>
                  </Tooltip>
                  <Popover open={Boolean(externAnchor)} anchorEl={externAnchor} onClose={() => setExternAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    PaperProps={{ sx: { p: 2, maxWidth: 520, borderRadius: '0.75rem' } }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                      Externe crew nog koppelen in Airtable
                    </Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, mb: 1.5, lineHeight: 1.45 }}>
                      Deze namen staan in de titel van de Outlook-afspraak, maar niet in het Crew-veld van de draaidag. Externe crew
                      nodigen we bewust niet uit in Outlook (dan krijgen ze bij elke wijziging een mail), dus koppel ze handmatig in
                      Airtable. Daarna kloppen callsheets, materiaal en de inkoop in de forecast.
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
                      {externeKoppelingen.map((e) => (
                        <ListItem key={e.record.id} disableGutters sx={{ alignItems: 'flex-start', py: 0.5, borderBottom: `1px solid ${T.outlineVariant}33` }}>
                          <ListItemText disableTypography
                            primary={(
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.76rem', fontWeight: 600, color: T.text }}>
                                {moment(e.startStr).format('dd D MMM')} · {e.title}
                              </Typography>
                            )}
                            secondary={(
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: '#b45309' }}>
                                  {e.crewNietGekoppeld.map((x) => `${x.naam}${x.status ? ` (${x.status})` : ''}`).join(', ')}
                                </Typography>
                                {e.openRollen?.length > 0 && (
                                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary }}>
                                    nog in te vullen: {e.openRollen.join(', ')}
                                  </Typography>
                                )}
                                {e.airtableUrl && (
                                  <a href={e.airtableUrl} target="_blank" rel="noreferrer" style={{ fontFamily: 'inherit', fontSize: '0.7rem', color: T.primary, fontWeight: 600 }}>
                                    Open in Airtable ↗
                                  </a>
                                )}
                              </Box>
                            )} />
                        </ListItem>
                      ))}
                    </List>
                  </Popover>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                kleur = reserveringsstatus · klik op een draaidag om materiaal te reserveren
              </Typography>
            </Box>
          )}
          {selectedDraaidag ? (
            <MaterialenOverzicht updateDraaidagLocally={updateDraaidagLocally} draaidagen={draaidagen} fetchDraaidagenFromBackend={fetchDraaidagenFromBackend} />
          ) : currentView !== 'agenda' ? (
            <>
            {/* Popover moved outside view blocks */}
            <Box sx={{
              mt: '5px',
              position: 'relative',
              // Fade + spinner overlay while reservations are loading
              ...(loading && {
                '&::after': {
                  content: '""',
                  position: 'absolute', inset: 0, zIndex: 5,
                  bgcolor: 'rgba(255,255,255,0.6)',
                  borderRadius: '0.75rem',
                  backdropFilter: 'blur(1px)',
                },
              }),
              // Stitch design system styling for FullCalendar
              '& .fc': { fontFamily: '"Inter", "Plus Jakarta Sans", system-ui, sans-serif', fontSize: '0.8rem' },
              '& .fc-toolbar': { flexWrap: 'wrap', gap: '8px' },
              '& .fc-toolbar-title': {
                fontSize: { xs: '0.95rem !important', sm: '1.15rem !important' }, fontWeight: 700,
                fontFamily: '"Manrope", system-ui, sans-serif',
                color: '#2d3335',
              },
              '& .fc-toolbar': { mb: 1 },
              '& .fc-button-primary': {
                backgroundColor: '#fff !important', borderColor: '#e2e8f0 !important',
                color: '#526075 !important', fontSize: '0.75rem !important',
                fontWeight: '600 !important', borderRadius: '0 !important',
                padding: '6px 16px !important', textTransform: 'capitalize !important',
                transition: 'all 0.15s !important',
                boxShadow: 'none !important',
                '&:hover': { backgroundColor: '#f1f4f5 !important', borderColor: '#e2e8f0 !important', color: '#2d3335 !important' },
                '&:focus': { boxShadow: 'none !important' },
                '&.fc-button-active': {
                  backgroundColor: '#526075 !important', borderColor: '#526075 !important',
                  color: '#fff !important',
                  '&:hover': { backgroundColor: '#465469 !important', borderColor: '#465469 !important', color: '#fff !important' },
                },
              },
              '& .fc-button-group': { borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' },
              '& .fc-button-group .fc-button-primary': { border: 'none !important' },
              '& .fc-day-today': {
                backgroundColor: 'rgba(99,89,131,0.06) !important',
                // Pil met eigen padding: de algemene 6px/8px-padding hieronder maakte de
                // 26px-cirkel een ovaal van ~42×38 dat over de eerste draaidag viel, en in
                // de 2-wekenweergave staat er '22 juni' in (tool-feedback #38).
                '& .fc-daygrid-day-number': {
                  color: '#fff !important',
                  backgroundColor: '#635983',
                  borderRadius: '13px',
                  minWidth: 26, height: 26, padding: '0 8px', margin: '4px 4px 0 0',
                  boxSizing: 'border-box', whiteSpace: 'nowrap', lineHeight: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '800 !important',
                },
              },
              '& .fc-daygrid-day-number': {
                fontSize: '0.78rem', fontWeight: 600, padding: '6px 8px',
                color: '#526075',
              },
              // Weekend columns: smaller + dimmed
              '& .fc-day-sat, & .fc-day-sun, & .fc-col-header-cell.fc-day-sat, & .fc-col-header-cell.fc-day-sun': {
                width: '6%',
              },
              '& .fc-day-sat, & .fc-day-sun': {
                backgroundColor: '#f8f9fa !important',
                '& .fc-daygrid-day-number': { color: '#adb3b5' },
              },
              '& .fc-event': {
                // Pil zoals op de draaidagen-kalender: licht getint, vlak, geen schaduw
                // Een tandje groter en max. 2 regels (feedback Joris 30 sep)
                borderRadius: '6px !important', fontSize: `${PIL_FONT} !important`,
                padding: '3px 6px !important', cursor: 'pointer',
                lineHeight: `${PIL_REGELHOOGTE} !important`, border: 'none !important',
                fontWeight: '600 !important',
                '&:hover': { filter: 'brightness(0.96)' },
              },
              // Jouw draaidag: rand links in de tekstkleur van de status (geen tweede kleurenset)
              // Jouw draaidag: rand links in de tekstkleur van de status (per status, zie DraaidagPil)
              ...JOUW_RAND_CSS,
              // Optie (nog niet bevestigd): zelfde statuskleur, maar gearceerd en lichter — zoals
              // de optie-draaidag in Rooster/Dagstart.
              '& .fc-event.optie-draaidag:not(.has-extra-days)': { backgroundImage: OPTIE_ARCERING },
              '& .fc-daygrid-event-dot': { display: 'none' },
              // FullCalendar zet daygrid-events op nowrap; de titel mag hier over 2 regels
              // (line-clamp in DraaidagPil). De event-hoogte groeit dan vanzelf mee.
              '& .fc-daygrid-event': { overflow: 'hidden', whiteSpace: 'normal !important' },
              '& .fc-daygrid-event .fc-event-main': { whiteSpace: 'normal' },
              '& .fc-daygrid-day': { minWidth: 0 },
              '& .has-extra-days': {
                backgroundColor: 'transparent !important',
                border: 'none !important',
                boxShadow: 'none !important',
                padding: '0 !important',
                overflow: 'visible !important',
              },
              '& .has-extra-days .fc-event-main': {
                padding: '0 !important',
              },
              '& .fc-daygrid-day': {
                transition: 'background-color 0.15s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(82,96,117,0.06) !important',
                },
                '&.fc-day-today:hover': {
                  backgroundColor: 'rgba(99,89,131,0.10) !important',
                },
              },
              '& .fc-daygrid-day-frame': { minHeight: 95 },
              '& .fc-col-header-cell-cushion': {
                fontWeight: 700, fontSize: '0.7rem', color: '#556071',
                textTransform: 'lowercase', letterSpacing: '0.02em',
                padding: '8px 0 !important',
              },
              '& .fc-scrollgrid': { borderColor: '#e5e7eb', borderRadius: '8px', overflow: 'hidden' },
              '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: 'rgba(45,51,53,0.06)' },
              '& .fc-daygrid-more-link': { fontSize: '0.65rem', color: '#635983', fontWeight: 700 },
              '& .fc-daygrid-week-number': {
                fontSize: '0.62rem', fontWeight: 700, color: '#adb3b5',
                padding: '4px 6px',
              },
            }}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                initialView={currentView === 'work_week' ? 'twoWeek' : 'dayGridMonth'}
                initialDate={currentDate}
                locale={nlLocale}
                views={{
                  twoWeek: {
                    type: 'dayGrid',
                    duration: { weeks: 2 },
                    buttonText: '2 Weken',
                    dayMaxEvents: false,
                  },
                }}
                headerToolbar={false}
                weekNumbers={true}
                weekNumberFormat={{ week: 'numeric' }}
                events={(() => {
                  const addDay = (dateStr, n) => { const d = new Date(dateStr + 'T12:00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
                  const result = [];
                  events.forEach(evt => {
                    const id = evt.record?.id || String(Math.random());
                    const bgColor = evt.style?.backgroundColor || '#3174ad';
                    const heeftVoor = draaidagenMetExtraDagen.extraDagVoor.has(evt.record?.id);
                    const heeftNa = draaidagenMetExtraDagen.extraDagNa.has(evt.record?.id);

                    // Extend the main event's date range to include extra days
                    const effectiveStart = heeftVoor ? addDay(evt.startStr, -1) : evt.startStr;
                    const effectiveEnd = heeftNa ? addDay(evt.endStr, 1) : evt.endStr; // endStr is already exclusive +1

                    result.push({
                      id,
                      title: evt.title,
                      start: effectiveStart,
                      end: effectiveEnd,
                      allDay: true,
                      backgroundColor: bgColor,
                      borderColor: bgColor,
                      textColor: evt.style?.color || '#fff',
                      // Alleen losse reserveringen zijn sleepbaar (verplaatsen); draaidagen niet
                      editable: !!evt.isLoose,
                      startEditable: !!evt.isLoose,
                      durationEditable: !!evt.isLoose,
                      classNames: [...((heeftVoor || heeftNa) ? ['has-extra-days'] : []), ...(evt.isLoose ? ['loose-event'] : []), ...(evt.style?.jouw ? ['jouw-draaidag'] : []), ...(evt.style?.statusKlasse ? [evt.style.statusKlasse] : []), ...(evt.omschrijving?.optie ? ['optie-draaidag'] : [])],
                      extendedProps: {
                        record: evt.record,
                        loose: evt.isLoose ? evt.looseRes : null,
                        tooltipText: evt.tooltipText,
                        fullTitle: evt.fullTitle,
                        heeftExtraDagVoor: heeftVoor,
                        heeftExtraDagNa: heeftNa,
                        originalStart: evt.startStr,
                        originalEnd: evt.endStr,
                        crewNietGekoppeld: evt.crewNietGekoppeld || [],
                        mogelijkDubbel: !!evt.mogelijkDubbel,
                        omschrijving: evt.omschrijving || null,
                      },
                    });
                  });
                  return result;
                })()}
                editable={LOSSE_RESERVERINGEN_ENABLED}
                dayMaxEvents={4}
                moreLinkText={(n) => `+${n} meer`}
                firstDay={1}
                height="auto"
                contentHeight="auto"
                aspectRatio={1.8}
                datesSet={(dateInfo) => {
                  const newDate = dateInfo.view.currentStart;
                  setCurrentDate(newDate);
                  const viewMap = { dayGridMonth: 'month', twoWeek: 'work_week', listMonth: 'agenda' };
                  const newView = viewMap[dateInfo.view.type] || dateInfo.view.type;
                  setCurrentView(newView);
                  try {
                    sessionStorage.setItem('calendarDate', newDate.toISOString());
                    sessionStorage.setItem('calendarView', newView);
                  } catch (e) { /* ignore */ }

                  // Week view: make day cells taller so all events are visible
                  requestAnimationFrame(() => {
                    const el = dateInfo.view.calendar.el;
                    if (!el) return;
                    const isWeekView = dateInfo.view.type === 'twoWeek';
                    el.querySelectorAll('.fc-daygrid-day-frame').forEach(f => {
                      f.style.minHeight = isWeekView ? '300px' : '';
                    });
                  });
                }}
                eventClick={(info) => {
                  // Ctrl/Cmd-klik: de <Link> in de pil opent een nieuw tabblad; hier niets doen
                  const loose = info.event.extendedProps.loose;
                  if (!loose && (info.jsEvent?.ctrlKey || info.jsEvent?.metaKey)) return;
                  if (loose) { startStandalone({ name: loose.name, start: loose.start, end: loose.end, reservationId: loose.id }); return; }
                  const record = info.event.extendedProps.record;
                  if (record) handleDraaidagClick(record);
                }}
                eventDrop={(info) => {
                  // Alleen losse reserveringen mogen verplaatst worden
                  const loose = info.event.extendedProps.loose;
                  if (!loose) { info.revert(); return; }
                  const dStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const newStart = dStr(info.event.start);
                  // FullCalendar end is exclusief → trek 1 dag af; geen end = 1-daags
                  let newEnd = newStart;
                  if (info.event.end) { const e = new Date(info.event.end); e.setDate(e.getDate() - 1); newEnd = dStr(e); }
                  // We openen de detailpagina met de nieuwe datum klaargezet → drag visueel terugzetten
                  info.revert();
                  startStandalone({ name: loose.name, start: loose.start, end: loose.end, reservationId: loose.id, moveTo: { start: newStart, end: newEnd } });
                }}
                eventResize={(info) => {
                  const loose = info.event.extendedProps.loose;
                  if (!loose) { info.revert(); return; }
                  const dStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const newStart = dStr(info.event.start);
                  let newEnd = newStart;
                  if (info.event.end) { const e = new Date(info.event.end); e.setDate(e.getDate() - 1); newEnd = dStr(e); }
                  info.revert();
                  startStandalone({ name: loose.name, start: loose.start, end: loose.end, reservationId: loose.id, moveTo: { start: newStart, end: newEnd } });
                }}
                dateClick={LOSSE_RESERVERINGEN_ENABLED ? (info) => {
                  // Klik op een dag → nieuwe losse reservering aanmaken (datum voorgevuld)
                  setLooseForm({ name: '', start: info.dateStr, end: info.dateStr });
                  setLooseOpen(true);
                } : undefined}
                eventContent={(arg) => {
                  const record = arg.event.extendedProps.record;
                  const draaidagId = record?.id;
                  const heeftVoor = arg.event.extendedProps.heeftExtraDagVoor;
                  const heeftNa = arg.event.extendedProps.heeftExtraDagNa;
                  const bgColor = arg.event.backgroundColor || '#3174ad';
                  const txtColor = arg.event.textColor || '#fff';
                  const hasExtra = heeftVoor || heeftNa;
                  const acctId = record?.fields?.AccountRecordId;
                  const acctInfo = acctId ? accountCache[acctId] : null;
                  const oms = arg.event.extendedProps.omschrijving;
                  const weer = draaidagId ? weerPerDraaidag[draaidagId] : null;
                  const pilInhoud = (
                    <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                      <DraaidagPilInhoud titel={oms?.hoofd || arg.event.title} labels={oms?.labels} meta={oms?.meta}
                        logoUrl={acctInfo?.beeldmerkUrl}
                        crewWaarschuwing={arg.event.extendedProps.crewNietGekoppeld?.length > 0}
                        dubbelWaarschuwing={!!arg.event.extendedProps.mogelijkDubbel} />
                      <WeerChip weer={weer} />
                    </Box>
                  );

                  if (!hasExtra) {
                    // Gewone pil: klantlogo + korte titel, tooltip-kaart erboven
                    return (
                      <Tooltip {...TOOLTIP_KAART_PROPS}
                        title={<EventTooltipContent tooltipText={arg.event.extendedProps.tooltipText || arg.event.title} weer={weer} />}
                      >
                        <Link
                          to={`/MaterialReservation?draaidagId=${draaidagId}`}
                          style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'flex-start', width: '100%', overflow: 'hidden', whiteSpace: 'normal', fontSize: 'inherit', lineHeight: PIL_REGELHOOGTE }}
                          onClick={(e) => { if (!e.ctrlKey && !e.metaKey && e.button !== 1) e.preventDefault(); }}
                        >
                          {pilInhoud}
                        </Link>
                      </Tooltip>
                    );
                  }

                  // Event with extra days — segmented bar
                  return (
                    <Tooltip {...TOOLTIP_KAART_PROPS}
                      title={<EventTooltipContent tooltipText={arg.event.extendedProps.tooltipText || arg.event.title} weer={weer} />}
                    >
                      <Link
                        to={`/MaterialReservation?draaidagId=${draaidagId}`}
                        style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'stretch', width: '100%', height: '100%', lineHeight: PIL_REGELHOOGTE, overflow: 'hidden', borderRadius: '4px' }}
                        onClick={(e) => { if (!e.ctrlKey && !e.metaKey && e.button !== 1) e.preventDefault(); }}
                      >
                        <ExtraDagenBalk
                          startStr={arg.event.startStr} endStr={arg.event.endStr}
                          isStart={arg.isStart} isEnd={arg.isEnd}
                          heeftVoor={heeftVoor} heeftNa={heeftNa}
                          bgColor={bgColor} txtColor={txtColor} optie={!!oms?.optie}
                        >
                          {pilInhoud}
                        </ExtraDagenBalk>
                      </Link>
                    </Tooltip>
                  );
                }}
              />
              {loading && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, border: `3px solid ${T.outlineVariant}`, borderTop: `3px solid ${T.tertiary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 600, color: T.secondary }}>
                    Reserveringen laden…
                  </Typography>
                </Box>
              )}
            </Box>
            </>
          ) : null}

          {/* Custom list view — replaces FullCalendar listMonth */}
          {currentView === 'agenda' && !selectedDraaidag && (
            <>
            {/* List items grouped by date */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(() => {
                const month = currentDate.getMonth();
                const year = currentDate.getFullYear();
                const monthEvents = events.filter(evt => {
                  const s = evt.start;
                  return s && s.getMonth() === month && s.getFullYear() === year;
                }).sort((a, b) => a.start - b.start || (a.record?.id || '').localeCompare(b.record?.id || ''));

                // Group by date string
                const grouped = {};
                monthEvents.forEach(evt => {
                  const key = evt.startStr;
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(evt);
                });

                const todayStr = formatDateISO(new Date());
                return Object.entries(grouped).map(([dateStr, dayEvents]) => {
                  const date = new Date(dateStr + 'T12:00:00');
                  const dayLabel = date.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
                  const isToday = dateStr === todayStr;
                  return (
                    <Box key={dateStr}>
                      <Box sx={{ px: 1.5, py: 0.75, bgcolor: isToday ? T.primaryContainer : T.surfaceLow, borderBottom: `1px solid ${T.outlineVariant}20`, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: T.font, fontSize: '0.78rem', fontWeight: 700, color: isToday ? T.primary : T.text, textTransform: 'capitalize' }}>
                          {dayLabel}
                        </Typography>
                        {isToday && (
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700, color: T.primary, bgcolor: T.surface, px: 0.75, py: 0.15, borderRadius: '9999px' }}>
                            Vandaag
                          </Typography>
                        )}
                      </Box>
                      {dayEvents.map(evt => {
                        const record = evt.record;
                        const bgColor = evt.style?.backgroundColor || T.outline;
                        const acctId = record?.fields?.AccountRecordId;
                        const acctInfo = acctId ? accountCache[acctId] : null;
                        // Reservation status for this draaidag
                        const matchRes = reservations?.find(r => r?.fields?.IDdraaidagOutlook?.[0] === record?.id);
                        const hasRes = !!matchRes;
                        const hasMats = hasRes && Array.isArray(matchRes?.fields?.Materiaal) && matchRes.fields.Materiaal.length > 0;
                        const isGeenNodig = (hasRes && !hasMats) || record?.fields?.['Geen Materiaal Nodig'];
                        const isDubbel = draaidagenMetDubbeleReserveringen.includes(record?.id);
                        return (
                          <Box key={evt.isLoose ? `loose-${evt.looseRes?.id}` : (record?.id || evt.title)}
                            onClick={(e) => {
                              if (evt.isLoose) { startStandalone({ name: evt.looseRes.name, start: evt.looseRes.start, end: evt.looseRes.end, reservationId: evt.looseRes.id }); return; }
                              // Ctrl/Cmd-klik: in een nieuw tabblad, net als in de kalender
                              if ((e.ctrlKey || e.metaKey) && record?.id) { window.open(`/MaterialReservation?draaidagId=${record.id}`, '_blank', 'noopener'); return; }
                              handleDraaidagClick(record);
                            }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.85,
                              borderBottom: `1px solid ${T.outlineVariant}12`, cursor: 'pointer',
                              transition: 'all 0.1s', '&:hover': { bgcolor: T.surfaceLow } }}>
                            {/* Status-chip: zelfde als in de tooltip-kaart (DraaidagPil) */}
                            <ReserveringStatusChip breedte={104}
                              status={evt.isLoose ? 'Losse reservering' : isDubbel ? 'Dubbel' : isGeenNodig ? 'Niet nodig' : hasRes ? 'Gereserveerd' : 'Open'} />
                            <Box sx={{ width: 16, flexShrink: 0, display: 'flex' }}>
                              <LogoMetTerugval src={acctInfo?.beeldmerkUrl}
                                sx={{ width: 16, height: 16, borderRadius: '2px', objectFit: 'contain' }} />
                            </Box>
                            {/* Zelfde hiërarchie als de pil: hoofdregel + kleine meta-regel */}
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', fontFamily: T.fontBody, fontSize: PIL_FONT, lineHeight: PIL_REGELHOOGTE, color: T.text }}>
                              <DraaidagOmschrijvingTekst
                                hoofd={evt.omschrijving?.hoofd || evt.title}
                                labels={evt.omschrijving?.labels}
                                meta={evt.omschrijving?.meta} />
                            </Box>
                            {hasMats && (
                              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant, flexShrink: 0 }}>
                                {matchRes.fields.Materiaal.length}x
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                });
              })()}
            </Box>
            </>
          )}
          {/* Shared Legenda Popover */}
          <Popover
            open={!!legendaAnchor}
            anchorEl={legendaAnchor}
            onClose={() => setLegendaAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '0.75rem', mt: 0.5, boxShadow: '0 8px 32px rgba(45,51,53,0.14), 0 1px 4px rgba(45,51,53,0.08)', border: `1px solid ${T.outlineVariant}30` } }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
              <Box>
                <Typography sx={{ fontFamily: T.font, fontSize: '0.6rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Kleur = reserveringsstatus</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  {[
                    ['Gereserveerd', 'materiaal gereserveerd of niet nodig'],
                    ['Open', 'nog geen materiaal'],
                    ['Dubbel', 'twee reserveringen op één draaidag'],
                    ...(LOSSE_RESERVERINGEN_ENABLED ? [['Losse reservering', 'zonder draaidag']] : []),
                  ].map(([st, uitleg]) => (
                    <Box key={st} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 34, height: 14, borderRadius: '4px', bgcolor: RESERVERING_STATUS[st].bg, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600, color: T.text }}>{st}</Typography>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.secondary }}>{uitleg}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: T.font, fontSize: '0.6rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Verder</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 34, height: 14, borderRadius: '4px', bgcolor: RESERVERING_STATUS.Open.bg, boxShadow: `inset 3px 0 0 ${RESERVERING_STATUS.Open.fg}`, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary }}>Jouw draaidag (jij staat op camera)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 34, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#d97706' }} />
                    </Box>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary }}>Externe crew nog koppelen in Airtable</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 34, display: 'flex', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, color: T.secondary, fontFamily: T.fontBody, flexShrink: 0 }}>−1 / +1</Box>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary }}>Extra dag vóór of ná (gestreept)</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Popover>
        </Grid>
      </Grid>
    );

  });
